import React, { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { videoCatalog, getVideoBySlug, getVideoUrl } from '../data/videoData';
import { isFullscreenActive, requestFullscreen, exitFullscreen } from '../lib/fullscreen';

const VideoLibrary = ({ productId = 'all', embedded = false, defaultVideoSlug = null, singleVideoOnly = false, layout = 'standard', enableAutoplay = (layout === 'grid') }) => {
  const [searchParams] = useSearchParams();
  const [activeFilter, setActiveFilter] = useState('all');

  const isAll = !productId || productId === 'all';
  const data = isAll
    ? {
        productId: 'all',
        badge: 'Alle videoer',
        productName: 'MedicCare Videobibliotek',
        productPath: '/video',
        title: 'Alle instruktions- og præsentationsvideoer',
        subtitle: 'Få et samlet overblik over alle demonstrations- og instruktionsvideoer for TIM, FlexSmartApp og Mobili®.',
        sections: Object.values(videoCatalog).flatMap(prod => 
          prod.sections.map(sec => ({
            title: `${prod.productName} – ${sec.title}`,
            productName: prod.productName,
            productId: prod.productId,
            badge: prod.badge,
            videos: sec.videos
          }))
        )
      }
    : videoCatalog[productId];

  if (!data) return null;

  // Normalize sections so we can handle both flat lists and categorized lists
  const sections = data.sections || [
    {
      title: 'Vælg en video',
      videos: data.videos || []
    }
  ];

  // Flatten all videos in this product/catalog to easily manage active state and navigation
  const allProductVideos = sections.flatMap(s => s.videos);

  // Filter sections when in "all" mode and a specific product is selected
  const visibleSections = isAll && activeFilter !== 'all'
    ? sections.filter(sec => sec.productId === activeFilter)
    : sections;

  const visibleVideos = visibleSections.flatMap(s => s.videos);

  // Determine initial video from URL query parameter ?v=... if present
  const queryVideoParam = searchParams.get('v') || searchParams.get('video');
  const matchedQueryVideo = queryVideoParam 
    ? allProductVideos.find(v => 
        v.slug === queryVideoParam || 
        v.id === queryVideoParam || 
        (v.aliases && v.aliases.includes(queryVideoParam))
      )
    : null;
  
  // Default to initial video: if defaultVideoSlug is provided, find it; otherwise query param, otherwise first video
  const defaultVideo = defaultVideoSlug 
    ? allProductVideos.find(v => v.slug === defaultVideoSlug || v.id === defaultVideoSlug)
    : (singleVideoOnly
        ? allProductVideos.find(v => v.slug === 'hvad-er-tim')
        : (productId === 'tim' ? (allProductVideos.find(v => v.slug === 'hvad-er-tim') || allProductVideos[0]) : allProductVideos[0]));

  const [activeVideo, setActiveVideo] = useState(matchedQueryVideo || defaultVideo || allProductVideos[0]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [realDurations, setRealDurations] = useState({});

  const formatSecs = (seconds) => {
    if (!seconds || isNaN(seconds) || !isFinite(seconds)) return null;
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getDuration = (vid) => {
    if (!vid) return '';
    return realDurations[vid.id] || vid.duration || '';
  };
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedVideoId, setCopiedVideoId] = useState(null);
  const [modalVideo, setModalVideo] = useState(null);
  const videoRef = useRef(null);
  const modalVideoRef = useRef(null);
  const modalContainerRef = useRef(null);
  const modalPlayerBoxRef = useRef(null);
  const standardPlayerBoxRef = useRef(null);
  const singlePlayerBoxRef = useRef(null);
  const playerContainerRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isWindowedFullscreen, setIsWindowedFullscreen] = useState(false);
  const wasFullscreenRef = useRef(false);
  const pendingFullscreenRef = useRef(false);
  const currentModalVideoIdRef = useRef(null);

  // Combined fullscreen status (native browser fullscreen OR windowed/CSS full-screen fallback)
  const isAnyFullscreen = isFullscreen || isWindowedFullscreen;

  // Exit fullscreen completely
  const exitFullscreenMode = async () => {
    setIsWindowedFullscreen(false);
    setIsFullscreen(false);
    wasFullscreenRef.current = false;
    const activeVideoEl = modalVideo ? modalVideoRef.current : videoRef.current;
    await exitFullscreen(activeVideoEl);
  };

  // Enter fullscreen with native-first then immediate windowed fallback
  const enterFullscreenMode = async () => {
    wasFullscreenRef.current = true;
    const targetContainer = modalVideo
      ? (modalContainerRef.current || modalPlayerBoxRef.current)
      : (singleVideoOnly ? singlePlayerBoxRef.current : standardPlayerBoxRef.current);
    const targetVideo = modalVideo ? modalVideoRef.current : videoRef.current;

    let nativeSuccess = false;
    try {
      nativeSuccess = await requestFullscreen(targetContainer, targetVideo);
    } catch {
      nativeSuccess = false;
    }

    if (nativeSuccess && isFullscreenActive()) {
      setIsFullscreen(true);
      setIsWindowedFullscreen(false);
    } else {
      // If native fullscreen is restricted, denied (e.g. preview iframe, iOS, permission policy),
      // windowed fullscreen takes over immediately so the container fills the entire screen!
      setIsWindowedFullscreen(true);
    }
  };

  // Fullscreen toggle for player (stor skærm)
  const toggleFullscreen = async (e) => {
    if (e) {
      if (typeof e.preventDefault === 'function') e.preventDefault();
      if (typeof e.stopPropagation === 'function') e.stopPropagation();
    }

    if (isAnyFullscreen || isFullscreenActive()) {
      await exitFullscreenMode();
    } else {
      await enterFullscreenMode();
    }
  };

  // Close modal and exit fullscreen if active
  const closeModal = () => {
    exitFullscreenMode();
    setIsWindowedFullscreen(false);
    wasFullscreenRef.current = false;
    setModalVideo(null);
    cancelCountdown();
    setEndScreen(null);
  };

  // Listen for fullscreen state changes (browser native + iOS webkit)
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFs = isFullscreenActive();
      setIsFullscreen(isFs);
      if (!isFs) {
        if (!wasFullscreenRef.current) {
          setIsWindowedFullscreen(false);
        }
      } else {
        wasFullscreenRef.current = true;
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  // Keyboard shortcut (F for fullscreen, Escape to exit or close) when modal is open
  useEffect(() => {
    if (!modalVideo) return;

    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      if (e.key === 'f' || e.key === 'F') {
        e.preventDefault();
        toggleFullscreen();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        closeModal();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [modalVideo, isAnyFullscreen]);

  // Video viewer always starts and stays in full screen
  useEffect(() => {
    if (modalVideo) {
      pendingFullscreenRef.current = false;
      wasFullscreenRef.current = true;
      setIsWindowedFullscreen(true);
      enterFullscreenMode();
    }
  }, [modalVideo]);

  // Helper to switch modal video and preserve fullscreen and autoplay
  const switchModalVideo = (nextVid, keepFullscreen = true) => {
    if (!nextVid) return;
    const shouldBeFs = keepFullscreen && (wasFullscreenRef.current || isAnyFullscreen || isFullscreenActive());
    setCountdown(null);
    setEndScreen(null);
    setModalVideo(nextVid);
    currentModalVideoIdRef.current = nextVid.id;

    const vid = modalVideoRef.current;
    if (vid) {
      vid.src = nextVid.url;
      if (nextVid.poster) vid.poster = nextVid.poster;
      vid.load();
      const p = vid.play();
      if (p !== undefined) {
        p.then(() => setIsPlaying(true)).catch((err) => {
          console.warn("Autoplay transition play error:", err);
        });
      }
    }

    if (shouldBeFs) {
      wasFullscreenRef.current = true;
      if (!isFullscreenActive()) {
        setIsWindowedFullscreen(true);
      }
      setTimeout(() => {
        const targetContainer = modalContainerRef.current || modalPlayerBoxRef.current;
        if (!isFullscreenActive() && targetContainer) {
          requestFullscreen(targetContainer, modalVideoRef.current)
            .then((ok) => {
              if (ok) setIsFullscreen(true);
            })
            .catch(() => {});
        }
      }, 70);
    }
  };

  // Keep video source updated without unmounting DOM element (preserves fullscreen during autoplay)
  useEffect(() => {
    if (modalVideo && modalVideoRef.current) {
      const vid = modalVideoRef.current;
      if (currentModalVideoIdRef.current !== modalVideo.id) {
        currentModalVideoIdRef.current = modalVideo.id;
        vid.src = modalVideo.url;
        if (modalVideo.poster) {
          vid.poster = modalVideo.poster;
        }
        vid.load();
        const p = vid.play();
        if (p !== undefined) {
          p.then(() => setIsPlaying(true)).catch(err => {
            console.log("Autoplay video transition error:", err);
          });
        }

        if (wasFullscreenRef.current || isAnyFullscreen || isFullscreenActive()) {
          if (!isFullscreenActive()) {
            setIsWindowedFullscreen(true);
          }
          setTimeout(() => {
            const targetContainer = modalContainerRef.current || modalPlayerBoxRef.current;
            if (!isFullscreenActive() && targetContainer) {
              requestFullscreen(targetContainer, vid)
                .then((ok) => {
                  if (ok) setIsFullscreen(true);
                })
                .catch(() => {});
            }
          }, 80);
        }
      }
    }
  }, [modalVideo, isAnyFullscreen]);

  // YouTube-style autoplay state (persisted in localStorage)
  const [autoplay, setAutoplay] = useState(() => {
    try {
      const saved = localStorage.getItem('mediccare_video_autoplay');
      return saved !== null ? saved === 'true' : true;
    } catch {
      return true;
    }
  });

  // Countdown state for auto-playing next video (5 seconds, YouTube style)
  const [countdown, setCountdown] = useState(null); // { seconds: number, nextVideo: object, target: 'modal' | 'standard' }
  const [endScreen, setEndScreen] = useState(null); // { nextVideo: object, target: 'modal' | 'standard' }

  const toggleAutoplay = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setAutoplay(prev => {
      const next = !prev;
      try {
        localStorage.setItem('mediccare_video_autoplay', String(next));
      } catch {}
      return next;
    });
  };

  // Helper to find the next video in numerical order
  const getNextVideo = (currentVideo, list = null) => {
    if (!currentVideo) return null;
    const pool = list || (
      visibleSections.find(sec => sec.videos.some(v => v.id === currentVideo.id))?.videos
      || allProductVideos
    );
    const idx = pool.findIndex(v => v.id === currentVideo.id);
    if (idx === -1) return pool[0] || null;
    return pool[(idx + 1) % pool.length];
  };

  // Helper to find previous video in numerical order
  const getPrevVideo = (currentVideo, list = null) => {
    if (!currentVideo) return null;
    const pool = list || (
      visibleSections.find(sec => sec.videos.some(v => v.id === currentVideo.id))?.videos
      || allProductVideos
    );
    const idx = pool.findIndex(v => v.id === currentVideo.id);
    if (idx === -1) return pool[0] || null;
    return pool[(idx - 1 + pool.length) % pool.length];
  };

  // Countdown timer effect
  useEffect(() => {
    if (!countdown) return;

    if (countdown.seconds <= 0) {
      const next = countdown.nextVideo;
      const target = countdown.target;
      setCountdown(null);
      setEndScreen(null);

      if (target === 'modal') {
        switchModalVideo(next, true);
      } else {
        const wasFs = wasFullscreenRef.current || isFullscreenActive();
        handleVideoSelect(next);
        if (wasFs) {
          setTimeout(() => {
            requestFullscreen(standardPlayerBoxRef.current, videoRef.current);
          }, 50);
        }
      }
      return;
    }

    const timer = setTimeout(() => {
      setCountdown(prev => prev ? { ...prev, seconds: prev.seconds - 1 } : null);
    }, 1000);

    return () => clearTimeout(timer);
  }, [countdown]);

  const cancelCountdown = () => {
    if (countdown) {
      setEndScreen({ nextVideo: countdown.nextVideo, target: countdown.target });
      setCountdown(null);
    }
  };

  const playNextNow = () => {
    const next = countdown?.nextVideo || endScreen?.nextVideo;
    const target = countdown?.target || endScreen?.target;
    if (next) {
      if (target === 'modal') {
        switchModalVideo(next, true);
      } else {
        const wasFs = wasFullscreenRef.current || isFullscreenActive();
        setCountdown(null);
        setEndScreen(null);
        handleVideoSelect(next);
        if (wasFs) {
          setTimeout(() => {
            requestFullscreen(standardPlayerBoxRef.current, videoRef.current);
          }, 50);
        }
      }
    }
  };

  const replayVideo = (target) => {
    setCountdown(null);
    setEndScreen(null);
    if (target === 'modal' && modalVideoRef.current) {
      modalVideoRef.current.currentTime = 0;
      modalVideoRef.current.play().then(() => setIsPlaying(true)).catch(() => {});
      if (wasFullscreenRef.current || isFullscreenActive()) {
        setTimeout(() => {
          if (modalPlayerBoxRef.current) {
            requestFullscreen(modalPlayerBoxRef.current, modalVideoRef.current);
          }
        }, 50);
      }
    } else if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().then(() => setIsPlaying(true)).catch(() => {});
    }
  };

  const handleModalVideoEnded = () => {
    if (!modalVideo) return;
    const next = getNextVideo(modalVideo);
    if (!next) return;

    if (autoplay) {
      setCountdown({
        seconds: 3,
        nextVideo: next,
        target: 'modal'
      });
    } else {
      setEndScreen({
        nextVideo: next,
        target: 'modal'
      });
    }
  };

  const handleStandardVideoEnded = () => {
    setIsPlaying(false);
    if (!enableAutoplay) return;
    if (!activeVideo) return;
    const next = getNextVideo(activeVideo);
    if (!next) return;

    if (autoplay) {
      setCountdown({
        seconds: 5,
        nextVideo: next,
        target: 'standard'
      });
    } else {
      setEndScreen({
        nextVideo: next,
        target: 'standard'
      });
    }
  };

  // Reset active video when product or query changes
  useEffect(() => {
    if (defaultVideoSlug) {
      const target = allProductVideos.find(v => v.slug === defaultVideoSlug || v.id === defaultVideoSlug);
      if (target) setActiveVideo(target);
    } else if (matchedQueryVideo) {
      setActiveVideo(matchedQueryVideo);
    } else if (defaultVideo) {
      setActiveVideo(defaultVideo);
    } else if (allProductVideos.length > 0 && !allProductVideos.some(v => v.id === activeVideo?.id)) {
      setActiveVideo(allProductVideos[0]);
    }
    setIsPlaying(false);
  }, [productId, queryVideoParam, defaultVideoSlug]);

  const handleVideoSelect = (video, autoStart = true) => {
    setActiveVideo(video);
    setIsPlaying(autoStart);
  };

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    } else {
      videoRef.current.play()
        .then(() => setIsPlaying(true))
        .catch((err) => {
          console.error("Play failed:", err);
          setIsPlaying(false);
        });
    }
  };

  const handleCopyVideoUrl = (videoToCopy) => {
    const target = videoToCopy || activeVideo;
    if (!target) return;
    const url = `${window.location.origin}/video/${target.slug}`;
    
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(() => {
        setCopiedLink(true);
        setTimeout(() => setCopiedLink(false), 2500);
      }).catch(() => {
        prompt('Kopiér linket herunder:', url);
      });
    } else {
      prompt('Kopiér linket herunder:', url);
    }
  };

  const handleCardPlay = (video, openInFullscreen = true) => {
    setCountdown(null);
    setEndScreen(null);
    if (layout === 'grid') {
      pendingFullscreenRef.current = true;
      wasFullscreenRef.current = true;
      setIsWindowedFullscreen(true);
      setModalVideo(video);
    } else {
      handleVideoSelect(video);
      if (openInFullscreen) {
        setTimeout(() => {
          requestFullscreen(standardPlayerBoxRef.current, videoRef.current);
        }, 60);
      } else if (playerContainerRef.current) {
        playerContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  const handleCardPlayFullscreen = (e, video) => {
    if (e) {
      if (typeof e.preventDefault === 'function') e.preventDefault();
      if (typeof e.stopPropagation === 'function') e.stopPropagation();
    }
    handleCardPlay(video, true);
  };

  const handleCardCopy = (e, video) => {
    e.preventDefault();
    e.stopPropagation();
    const url = `${window.location.origin}/video/${video.slug}`;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(() => {
        setCopiedVideoId(video.id);
        setTimeout(() => setCopiedVideoId(null), 2500);
      }).catch(() => {
        prompt('Kopiér linket herunder:', url);
      });
    } else {
      prompt('Kopiér linket herunder:', url);
    }
  };

  const renderContent = () => {
    if (layout === 'grid') {
      return renderGridContent();
    }
    return renderStandardContent();
  };
    if (singleVideoOnly) {
      return (
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-3xl overflow-hidden shadow-xl border border-slate-100 p-4 sm:p-6">
            <div 
              ref={singlePlayerBoxRef}
              onDoubleClick={toggleFullscreen}
              className="relative aspect-video bg-slate-950 rounded-2xl overflow-hidden group shadow-inner"
            >
              <video
                key={activeVideo?.id}
                ref={videoRef}
                src={activeVideo?.url}
                poster={activeVideo?.poster}
                className="w-full h-full object-cover"
                controls={isPlaying}
                autoPlay={isPlaying}
                playsInline
                preload="metadata"
                onPlay={() => {
                  setIsPlaying(true);
                  if (videoRef.current?.textTracks?.[0]) {
                    videoRef.current.textTracks[0].mode = 'showing';
                  }
                }}
                onLoadedMetadata={(e) => {
                  const el = e.currentTarget;
                  if (el?.duration && isFinite(el.duration)) {
                    const mins = Math.floor(el.duration / 60);
                    const secs = Math.floor(el.duration % 60);
                    const formatted = `${mins}:${secs.toString().padStart(2, '0')}`;
                    setRealDurations(prev => ({ ...prev, [activeVideo?.id]: formatted }));
                  }
                  if (el?.textTracks?.[0]) {
                    el.textTracks[0].mode = 'showing';
                  }
                }}
                onPause={() => setIsPlaying(false)}
                onEnded={() => setIsPlaying(false)}
              >
                {activeVideo?.subtitle && (
                  <track
                    key={activeVideo.subtitle}
                    kind="subtitles"
                    src={activeVideo.subtitle}
                    srcLang="da"
                    label="Dansk"
                    default
                  />
                )}
              </video>
              
              {/* Custom Play Overlay (only shown when not playing) */}
              {!isPlaying && activeVideo && (
                <div 
                  onClick={togglePlay}
                  className="absolute inset-0 flex flex-col items-center justify-center bg-slate-950/40 group-hover:bg-slate-950/50 transition-all cursor-pointer z-10"
                >
                  <div className="w-20 h-20 sm:w-24 sm:h-24 flex items-center justify-center bg-white text-[#2c3223] rounded-full shadow-2xl transform group-hover:scale-110 transition-all duration-300">
                    <i className="fa-solid fa-play text-2xl sm:text-3xl ml-1" />
                  </div>
                  <span className="mt-4 text-white font-semibold text-sm sm:text-base tracking-wide drop-shadow-md bg-slate-900/60 px-5 py-2 rounded-full backdrop-blur-xs">
                    Afspil video ({getDuration(activeVideo)})
                  </span>
                </div>
              )}
            </div>
            
            {/* Active Video Info & Direct Share Links */}
            {activeVideo && (
              <div className="pt-6 sm:pt-8 px-2 sm:px-4 pb-2">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-bold uppercase tracking-wider text-brand-dark bg-brand-soft px-3 py-1 rounded-md">
                      {activeVideo.productName || data.badge}
                    </span>
                    <span className="text-xs text-slate-500 flex items-center gap-1.5 font-medium">
                      <i className="fa-light fa-clock text-slate-400" /> {getDuration(activeVideo)} min
                    </span>
                  </div>

                  {/* Direct link & copy actions for currently selected video */}
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleCopyVideoUrl(activeVideo)}
                      className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                        copiedLink
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-300'
                          : 'bg-slate-50 hover:bg-slate-100 text-slate-700 border-slate-200'
                      }`}
                      title="Kopiér link til denne video"
                    >
                      <i className={`fa-solid ${copiedLink ? 'fa-check text-emerald-600' : 'fa-link text-slate-400'}`} />
                      <span>{copiedLink ? 'Link kopieret!' : 'Kopiér link'}</span>
                    </button>

                    <button
                      type="button"
                      onClick={toggleFullscreen}
                      className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-semibold border transition-all cursor-pointer shadow-2xs ${
                        isAnyFullscreen
                          ? 'bg-emerald-50 text-emerald-800 border-emerald-300 hover:bg-emerald-100'
                          : 'bg-slate-50 hover:bg-slate-100 text-slate-700 border-slate-200'
                      }`}
                      title={isAnyFullscreen ? 'Afslut stor skærm (Esc / F)' : 'Åbn i stor skærm (F)'}
                    >
                      <i className={`fa-solid ${isAnyFullscreen ? 'fa-compress text-emerald-600' : 'fa-expand text-slate-500'}`} />
                      <span>{isAnyFullscreen ? 'Minimer' : 'Stor skærm'}</span>
                    </button>

                    <a
                      href={getVideoUrl(activeVideo)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-medium bg-slate-50 hover:bg-slate-100 text-slate-700 border border-slate-200 transition-colors"
                      title="Åbn video i egen fane"
                    >
                      <i className="fa-light fa-arrow-up-right-from-square text-[11px] text-slate-400" />
                      <span>Åbn i ny fane</span>
                    </a>
                  </div>
                </div>

                <h3 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mb-3 tracking-tight">
                  {activeVideo.title}
                </h3>
                <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                  {activeVideo.longDescription || activeVideo.description}
                </p>
              </div>
            )}
          </div>
        </div>
      );
    }

  // YouTube-style Countdown & End-Screen Overlay
  const renderAutoplayCountdownOverlay = (target) => {
    if (countdown && countdown.target === target) {
      const next = countdown.nextVideo;
      return (
        <div className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-slate-950/90 text-white p-4 sm:p-6 backdrop-blur-md animate-in fade-in duration-200 select-none">
          {/* Top header with animated circular countdown */}
          <div className="flex flex-col items-center text-center mb-4 sm:mb-5 max-w-md">
            <div className="relative w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center mb-2">
              <svg className="w-14 h-14 sm:w-16 sm:h-16 transform -rotate-90" viewBox="0 0 36 36">
                <path
                  className="text-white/20"
                  strokeWidth="3.5"
                  stroke="currentColor"
                  fill="none"
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                />
                <path
                  className="text-emerald-400 transition-all duration-1000 ease-linear"
                  strokeDasharray="100, 100"
                  strokeDashoffset={`${((5 - countdown.seconds) / 5) * 100}`}
                  strokeLinecap="round"
                  strokeWidth="3.5"
                  stroke="currentColor"
                  fill="none"
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                />
              </svg>
              <span className="absolute font-bold text-xl sm:text-2xl text-white">
                {countdown.seconds}
              </span>
            </div>
            <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 text-[11px] font-semibold mb-1">
              <i className="fa-solid fa-forward-step text-[10px]" />
              Autoafspilning aktiv
            </div>
            <h4 className="text-sm sm:text-base md:text-lg font-bold text-white">
              Næste video starter om {countdown.seconds} {countdown.seconds === 1 ? 'sekund' : 'sekunder'}
            </h4>
          </div>

          {/* Next Video Preview Card */}
          {next && (
            <div 
              onClick={playNextNow}
              className="w-full max-w-md bg-white/10 hover:bg-white/15 border border-white/15 rounded-2xl p-3 sm:p-3.5 flex items-center gap-3.5 cursor-pointer transition-all duration-200 group mb-4 sm:mb-5 shadow-2xl"
              title="Klik for at afspille med det samme"
            >
              <div className="relative w-24 sm:w-28 aspect-video bg-black rounded-xl overflow-hidden shrink-0 border border-white/10">
                <img
                  src={next.poster}
                  alt={next.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-white/95 text-slate-900 flex items-center justify-center group-hover:scale-110 group-hover:bg-emerald-400 transition-all shadow-md">
                    <i className="fa-solid fa-play text-[10px] sm:text-[11px] ml-0.5" />
                  </div>
                </div>
                <span className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded text-[9px] font-bold bg-black/80 text-white">
                  {getDuration(next)} min
                </span>
              </div>

              <div className="min-w-0 flex-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-400 mb-0.5 block">
                  Næste i rækkefølgen
                </span>
                <h5 className="font-bold text-xs sm:text-sm text-white line-clamp-1 group-hover:text-emerald-300 transition-colors">
                  {next.title}
                </h5>
                <p className="text-[11px] sm:text-xs text-white/70 line-clamp-2 mt-0.5 leading-relaxed">
                  {next.description}
                </p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-wrap items-center justify-center gap-2.5">
            <button
              type="button"
              onClick={playNextNow}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-xs bg-emerald-500 hover:bg-emerald-400 text-slate-950 transition-all shadow-lg hover:scale-105 cursor-pointer"
            >
              <i className="fa-solid fa-play text-xs" />
              <span>Afspil nu</span>
            </button>

            <button
              type="button"
              onClick={cancelCountdown}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-xs bg-white/10 hover:bg-white/20 text-white transition-colors border border-white/20 cursor-pointer"
            >
              <i className="fa-solid fa-xmark text-xs" />
              <span>Annuller</span>
            </button>

            <button
              type="button"
              onClick={() => replayVideo(target)}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl font-semibold text-xs text-white/80 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
            >
              <i className="fa-solid fa-rotate-left text-xs" />
              <span>Se igen</span>
            </button>
          </div>
        </div>
      );
    }

    if (endScreen && endScreen.target === target) {
      const next = endScreen.nextVideo;
      return (
        <div className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-slate-950/85 text-white p-4 sm:p-6 backdrop-blur-md animate-in fade-in duration-200 select-none">
          <div className="text-center mb-4 sm:mb-5">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-2 text-white">
              <i className="fa-solid fa-check text-sm sm:text-base text-emerald-400" />
            </div>
            <h4 className="text-sm sm:text-base md:text-lg font-bold text-white">
              Video afsluttet
            </h4>
            <p className="text-xs text-slate-300 mt-1">
              Autoafspilning er slået fra
            </p>
          </div>

          {next && (
            <div 
              onClick={playNextNow}
              className="w-full max-w-md bg-white/10 hover:bg-white/15 border border-white/15 rounded-2xl p-3 sm:p-3.5 flex items-center gap-3.5 cursor-pointer transition-all duration-200 group mb-4 sm:mb-5 shadow-2xl"
            >
              <div className="relative w-24 sm:w-28 aspect-video bg-black rounded-xl overflow-hidden shrink-0 border border-white/10">
                <img
                  src={next.poster}
                  alt={next.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-white text-slate-900 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <i className="fa-solid fa-play text-[10px] sm:text-[11px] ml-0.5" />
                  </div>
                </div>
                <span className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded text-[9px] font-bold bg-black/80 text-white">
                  {getDuration(next)} min
                </span>
              </div>

              <div className="min-w-0 flex-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5 block">
                  Næste video i serien
                </span>
                <h5 className="font-bold text-xs sm:text-sm text-white line-clamp-1 group-hover:text-emerald-300 transition-colors">
                  {next.title}
                </h5>
                <p className="text-[11px] sm:text-xs text-white/70 line-clamp-2 mt-0.5 leading-relaxed">
                  {next.description}
                </p>
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center justify-center gap-2.5">
            {next && (
              <button
                type="button"
                onClick={playNextNow}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-xs bg-emerald-500 hover:bg-emerald-400 text-slate-950 transition-all shadow-lg cursor-pointer"
              >
                <i className="fa-solid fa-forward-step text-xs" />
                <span>Afspil næste video</span>
              </button>
            )}

            <button
              type="button"
              onClick={() => replayVideo(target)}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-xs bg-white/10 hover:bg-white/20 text-white transition-colors border border-white/20 cursor-pointer"
            >
              <i className="fa-solid fa-rotate-left text-xs" />
              <span>Se forfra</span>
            </button>
          </div>
        </div>
      );
    }

    return null;
  };

  const renderGridSection = () => {
    return (
        <div className="space-y-16">
          {/* Product Filter Tabs when viewing all */}
          {isAll && (
            <div className="flex flex-wrap justify-center gap-2 pb-2">
              {[
                { id: 'all', label: 'Alle' },
                { id: 'tim', label: 'TIM' },
                { id: 'flexsmartapp', label: 'FlexSmartApp' },
                { id: 'mobili', label: 'Mobili®' }
              ].map(tab => {
                const count = tab.id === 'all' 
                  ? allProductVideos.length 
                  : allProductVideos.filter(v => v.productId === tab.id).length;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveFilter(tab.id)}
                    className={`px-4 py-2 rounded-xl text-xs font-semibold transition-all ${
                      activeFilter === tab.id
                        ? 'bg-[#2c3223] text-white shadow-md'
                        : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                    }`}
                  >
                    {tab.label} <span className="text-[10px] opacity-75">({count})</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Sektioner opdelt i 3-på-linje gitter */}
          <div className="space-y-16">
            {visibleSections.map((section, sIdx) => {
              const isAnimated = section.title.toLowerCase().includes('animerede') || section.title.toLowerCase().includes('præsentation');
              return (
                <div key={sIdx} className="space-y-6">
                  {/* Sektion header med YouTube-style autoafspilningsbjælke */}
                  <div className="flex flex-col items-center text-center gap-3 pb-6 border-b border-slate-200">
                    <div className="flex items-center justify-center flex-wrap gap-3">
                      <h3 className="text-2xl font-extrabold text-slate-900 tracking-tight">
                        {section.title}
                      </h3>
                      <span className="text-xs font-semibold text-slate-600 bg-slate-100 px-3 py-1 rounded-full">
                        {section.videos.length} videoer
                      </span>
                    </div>
                    <p className="text-sm text-slate-500 max-w-2xl mx-auto">
                      {isAnimated 
                        ? 'Pædagogiske præsentationsfilm om TIM-systemet, demografien og borgerens tryghed i eget hjem.'
                        : 'Konkrete funktionsvideoer der demonstrerer betjening, funktioner og sikker medicinudlevering.'}
                    </p>

                    {/* YouTube-style kontrolbjælke til serie og autoafspilning */}
                    <div className="mt-2 flex flex-wrap items-center justify-center gap-2.5 sm:gap-4 bg-slate-50 border border-slate-200 px-4 py-2.5 rounded-2xl shadow-2xs">
                      <button
                        type="button"
                        onClick={() => {
                          if (section.videos.length > 0) {
                            cancelCountdown();
                            setEndScreen(null);
                            handleCardPlay(section.videos[0], true);
                          }
                        }}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold bg-[#2c3223] text-white hover:bg-[#3d4631] transition-all shadow-xs cursor-pointer"
                        title="Start serien i fuld skærm"
                      >
                        <i className="fa-solid fa-expand text-[10px]" />
                        <span>Afspil serie i fuld skærm (1–{section.videos.length})</span>
                      </button>

                      <div className="h-4 w-px bg-slate-200 hidden sm:block" />

                      {/* Autoplay Toggle Switch i YouTube stil */}
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                          <i className={`fa-solid ${autoplay ? 'fa-forward-step text-emerald-600' : 'fa-pause text-slate-400'}`} />
                          Autoafspilning:
                        </span>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={autoplay}
                          onClick={toggleAutoplay}
                          className={`relative inline-flex h-5 w-10 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                            autoplay ? 'bg-[#2c3223]' : 'bg-slate-300'
                          }`}
                          title={autoplay ? 'Autoafspilning er slået til (klik for at slå fra)' : 'Autoafspilning er slået fra (klik for at slå til)'}
                        >
                          <span
                            aria-hidden="true"
                            className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-xs transition duration-200 ease-in-out ${
                              autoplay ? 'translate-x-5' : 'translate-x-0'
                            }`}
                          />
                        </button>
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                          autoplay ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-600'
                        }`}>
                          {autoplay ? 'TIL' : 'FRA'}
                        </span>
                      </div>

                      <span className="text-[11px] text-slate-500 font-medium hidden md:inline-flex items-center gap-1">
                        <i className="fa-light fa-arrow-right text-[10px] text-slate-400" />
                        Afspilles automatisk i numerisk rækkefølge
                      </span>
                    </div>
                  </div>

                  {/* 3 på linje gitter */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                    {section.videos.map((video, vIdx) => {
                      const isSelected = video.id === activeVideo?.id;

                      return (
                        <div
                          key={video.id}
                          className={`bg-white rounded-2xl border transition-all duration-300 flex flex-col justify-between overflow-hidden group shadow-sm hover:shadow-xl ${
                            isSelected
                              ? 'border-[#2c3223] ring-2 ring-[#2c3223]/25 shadow-md'
                              : 'border-slate-200/80 hover:border-[#2c3223]/40'
                          }`}
                        >
                          {/* Thumbnail med play overlay */}
                          <div
                            onClick={() => handleCardPlay(video)}
                            className="relative aspect-video bg-slate-950 overflow-hidden cursor-pointer select-none"
                          >
                            <img
                              src={video.poster}
                              alt={video.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                            <div className={`absolute inset-0 transition-opacity duration-300 ${
                              isSelected ? 'bg-slate-950/20' : 'bg-slate-950/30 group-hover:bg-slate-950/40'
                            }`} />

                            {/* Centreret Play-knap */}
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className={`w-12 h-12 rounded-full flex items-center justify-center shadow-xl transition-all duration-300 group-hover:scale-110 ${
                                isSelected
                                  ? 'bg-[#2c3223] text-white ring-4 ring-white/30'
                                  : 'bg-white/95 text-[#2c3223] group-hover:bg-[#2c3223] group-hover:text-white'
                              }`}>
                                <i className={`fa-solid ${isSelected && isPlaying ? 'fa-pause text-base' : 'fa-play text-base ml-0.5'}`} />
                              </div>
                            </div>

                            {/* Top Badges: Delnummer og varighed */}
                            <div className="absolute top-3 left-3 pointer-events-none">
                              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md text-[11px] font-bold bg-white/95 text-slate-800 shadow-sm backdrop-blur-xs">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                Del {vIdx + 1} af {section.videos.length}
                              </span>
                            </div>

                            <div className="absolute top-3 right-3 pointer-events-none flex items-center gap-1.5">
                              {(video.subtitle || video.hasSubtitles) && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-bold bg-black/80 text-emerald-300 border border-emerald-500/40 backdrop-blur-xs shadow-xs" title="Undertekster tilgængelig">
                                  <i className="fa-solid fa-closed-captioning text-[10px]" /> CC
                                </span>
                              )}
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium bg-black/75 text-white backdrop-blur-xs">
                                <i className="fa-light fa-clock text-[10px]" /> {getDuration(video)} min
                              </span>
                            </div>
                          </div>

                          {/* Tekst og beskrivelse */}
                          <div className="p-5 flex flex-col flex-1 justify-between">
                            <div>
                              <h4
                                onClick={() => handleCardPlay(video)}
                                className="font-bold text-slate-900 group-hover:text-[#2c3223] text-base leading-snug cursor-pointer transition-colors mb-2 line-clamp-1"
                              >
                                {video.title}
                              </h4>
                              <p className="text-slate-600 text-xs sm:text-sm leading-relaxed line-clamp-2 mb-4">
                                {video.description}
                              </p>
                            </div>

                            {/* Afspil handling */}
                            <div className="pt-3 border-t border-slate-100 flex items-center justify-between">
                              <button
                                type="button"
                                onClick={() => handleCardPlay(video, true)}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold bg-[#2c3223] hover:bg-[#3d4631] text-white transition-all shadow-xs cursor-pointer"
                                title="Afspil video i fuld skærm"
                              >
                                <i className="fa-solid fa-expand text-[10px]" />
                                <span>Afspil i fuld skærm</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Fuldskærm afspiller */}
          {modalVideo && (
            <div 
              className="fixed inset-0 z-50 flex items-center justify-center p-0 bg-black"
              onClick={closeModal}
            >
              <div 
                ref={modalContainerRef}
                className="relative w-screen h-screen max-w-none max-h-none rounded-none border-0 overflow-hidden flex flex-col bg-black"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Diskret topbjælke med titel og lukkeknap */}
                <div className="flex items-center justify-between px-4 sm:px-6 py-2.5 bg-black/90 text-white border-b border-white/10 shrink-0">
                  <h3 className="font-semibold text-sm sm:text-base text-white/90 truncate pr-4">
                    {modalVideo.title}
                  </h3>

                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-medium transition-colors cursor-pointer"
                      title="Luk afspiller (Esc)"
                    >
                      <i className="fa-solid fa-xmark text-sm" />
                      <span>Luk</span>
                    </button>
                  </div>
                </div>

                {/* Video Container med video og autoplay overlay */}
                <div 
                  ref={modalPlayerBoxRef}
                  className="relative bg-black overflow-hidden flex-1 w-full min-h-0 flex items-center justify-center"
                >
                  <video
                    ref={modalVideoRef}
                    src={modalVideo.url}
                    poster={modalVideo.poster}
                    controls
                    autoPlay
                    playsInline
                    className="w-full h-full object-contain"
                    onPlay={(e) => {
                      const el = e.currentTarget;
                      if (el?.textTracks?.[0]) {
                        el.textTracks[0].mode = 'showing';
                      }
                    }}
                    onLoadedMetadata={(e) => {
                      const el = e.currentTarget;
                      if (el?.duration && isFinite(el.duration)) {
                        const mins = Math.floor(el.duration / 60);
                        const secs = Math.floor(el.duration % 60);
                        const formatted = `${mins}:${secs.toString().padStart(2, '0')}`;
                        setRealDurations(prev => ({ ...prev, [modalVideo?.id]: formatted }));
                      }
                      if (el?.textTracks?.[0]) {
                        el.textTracks[0].mode = 'showing';
                      }
                    }}
                    onEnded={handleModalVideoEnded}
                  >
                    {modalVideo.subtitle && (
                      <track
                        key={modalVideo.subtitle}
                        kind="subtitles"
                        src={modalVideo.subtitle}
                        srcLang="da"
                        label="Dansk"
                        default
                      />
                    )}
                  </video>

                  {/* YouTube Countdown Overlay */}
                  {renderAutoplayCountdownOverlay('modal')}
                </div>
              </div>
            </div>
          )}
        </div>
    );
  };

  const renderStandardContent = () => {
    const nextVideoInQueue = activeVideo ? getNextVideo(activeVideo) : null;

    return (
    <div ref={playerContainerRef} className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
      {/* Left: Main Video Player */}
      <div className="lg:col-span-7 xl:col-span-8">
        <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-slate-100 p-3">
          <div 
            ref={standardPlayerBoxRef}
            onDoubleClick={toggleFullscreen}
            className="relative aspect-video bg-slate-950 rounded-xl overflow-hidden group"
          >
            <video
              key={activeVideo?.id}
              ref={videoRef}
              src={activeVideo?.url}
              poster={activeVideo?.poster}
              className="w-full h-full object-cover"
              controls={isPlaying}
              autoPlay={isPlaying}
              playsInline
              preload="metadata"
              onPlay={() => {
                setIsPlaying(true);
                if (videoRef.current?.textTracks?.[0]) {
                  videoRef.current.textTracks[0].mode = 'showing';
                }
              }}
              onLoadedMetadata={(e) => {
                const el = e.currentTarget;
                if (el?.duration && isFinite(el.duration)) {
                  const mins = Math.floor(el.duration / 60);
                  const secs = Math.floor(el.duration % 60);
                  const formatted = `${mins}:${secs.toString().padStart(2, '0')}`;
                  setRealDurations(prev => ({ ...prev, [activeVideo?.id]: formatted }));
                }
                if (el?.textTracks?.[0]) {
                  el.textTracks[0].mode = 'showing';
                }
              }}
              onPause={() => setIsPlaying(false)}
              onError={() => setIsPlaying(false)}
              onEnded={handleStandardVideoEnded}
            >
              {activeVideo?.subtitle && (
                <track
                  key={activeVideo.subtitle}
                  kind="subtitles"
                  src={activeVideo.subtitle}
                  srcLang="da"
                  label="Dansk"
                  default
                />
              )}
            </video>
            
            {/* Custom Play Overlay (only shown when not playing) */}
            {!isPlaying && !countdown && !endScreen && activeVideo && (
              <div 
                onClick={togglePlay}
                className="absolute inset-0 flex flex-col items-center justify-center bg-slate-950/40 group-hover:bg-slate-950/50 transition-all cursor-pointer z-10"
              >
                <div className="w-20 h-20 flex items-center justify-center bg-white text-[#2c3223] rounded-full shadow-2xl transform group-hover:scale-110 transition-all duration-300">
                  <i className="fa-solid fa-play text-2xl ml-1" />
                </div>
                <span className="mt-4 text-white font-semibold text-sm tracking-wide drop-shadow-md bg-slate-900/60 px-4 py-1.5 rounded-full backdrop-blur-xs">
                  Afspil video ({getDuration(activeVideo)})
                </span>
              </div>
            )}

            {/* YouTube-style countdown overlay in standard view */}
            {enableAutoplay && renderAutoplayCountdownOverlay('standard')}
          </div>
          
          {/* Active Video Info & Direct Share Links */}
          {activeVideo && (
            <div className="p-5">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <div className="flex items-center gap-3">
                  <span className="text-xs font-bold uppercase tracking-wider text-brand-dark bg-brand-soft px-2.5 py-1 rounded-md">
                    {activeVideo.productName || data.badge}
                  </span>
                  <span className="text-xs text-slate-400 flex items-center gap-1">
                    <i className="fa-light fa-clock" /> {getDuration(activeVideo)} min
                  </span>
                </div>

                {/* Direct link, autoplay toggle & actions for currently selected video */}
                <div className="flex flex-wrap items-center gap-2">
                  {/* Autoplay Toggle Switch (kun når enableAutoplay er slået til) */}
                  {enableAutoplay && (
                    <div className="flex items-center gap-2 bg-slate-50 px-2.5 py-1 rounded-full border border-slate-200">
                      <span className="text-[11px] font-semibold text-slate-600 hidden sm:inline-flex items-center gap-1">
                        <i className={`fa-solid ${autoplay ? 'fa-forward-step text-emerald-600' : 'fa-pause text-slate-400'}`} />
                        Autoafspilning
                      </span>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={autoplay}
                        onClick={toggleAutoplay}
                        className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                          autoplay ? 'bg-[#2c3223]' : 'bg-slate-300'
                        }`}
                        title={autoplay ? 'Autoafspilning er til (klik for at slå fra)' : 'Autoafspilning er fra (klik for at slå til)'}
                      >
                        <span
                          aria-hidden="true"
                          className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-xs transition duration-200 ease-in-out ${
                            autoplay ? 'translate-x-4' : 'translate-x-0'
                          }`}
                        />
                      </button>
                      <span className={`text-[10px] font-bold ${autoplay ? 'text-emerald-700' : 'text-slate-500'}`}>
                        {autoplay ? 'TIL' : 'FRA'}
                      </span>
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => handleCopyVideoUrl(activeVideo)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors cursor-pointer ${
                      copiedLink
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-300'
                        : 'bg-slate-50 hover:bg-slate-100 text-slate-700 border-slate-200'
                    }`}
                    title="Kopiér link til denne video"
                  >
                    <i className={`fa-solid ${copiedLink ? 'fa-check text-emerald-600' : 'fa-link text-slate-400'}`} />
                    <span>{copiedLink ? 'Link kopieret!' : 'Kopiér link'}</span>
                  </button>

                  {/* Stor skærm knap på standard afspiller */}
                  <button
                    type="button"
                    onClick={toggleFullscreen}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all cursor-pointer shadow-2xs ${
                      isFullscreen
                        ? 'bg-emerald-50 text-emerald-800 border-emerald-300 hover:bg-emerald-100'
                        : 'bg-slate-50 hover:bg-slate-100 text-slate-700 border-slate-200'
                    }`}
                    title={isFullscreen ? 'Afslut stor skærm (Esc / F)' : 'Åbn i stor skærm (F)'}
                  >
                    <i className={`fa-solid ${isFullscreen ? 'fa-compress text-emerald-600' : 'fa-expand text-slate-500'}`} />
                    <span>{isFullscreen ? 'Minimer' : 'Stor skærm'}</span>
                  </button>

                  <a
                    href={getVideoUrl(activeVideo)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-50 hover:bg-slate-100 text-slate-700 border border-slate-200 transition-colors"
                    title="Åbn video i egen fane"
                  >
                    <i className="fa-light fa-arrow-up-right-from-square text-[11px] text-slate-400" />
                    <span>Åbn i ny fane</span>
                  </a>
                </div>
              </div>

              <h3 className="text-xl font-bold text-slate-900 mb-2">
                {activeVideo.title}
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                {activeVideo.longDescription || activeVideo.description}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Right: Playlist with Sections & Native Links */}
      <div className="lg:col-span-5 xl:col-span-4 flex flex-col pt-3">
        <div className="flex items-center justify-between border-b border-slate-100 pb-3 mb-4">
          <div>
            <h4 className="text-xs font-bold uppercase tracking-widest text-slate-400 flex items-center gap-2">
              <i className="fa-light fa-list-play text-sm" /> Spilleliste
            </h4>
          </div>
          <span className="text-xs text-slate-500 font-medium bg-slate-100 px-2.5 py-0.5 rounded-full shrink-0">
            {visibleVideos.length} videoer
          </span>
        </div>

        {/* Product Filter Tabs when viewing all */}
        {isAll && (
          <div className="flex flex-wrap gap-1.5 pb-1">
            {[
              { id: 'all', label: 'Alle' },
              { id: 'tim', label: 'TIM' },
              { id: 'flexsmartapp', label: 'FlexSmartApp' },
              { id: 'mobili', label: 'Mobili®' }
            ].map(tab => {
              const count = tab.id === 'all' 
                ? allProductVideos.length 
                : allProductVideos.filter(v => v.productId === tab.id).length;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveFilter(tab.id)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    activeFilter === tab.id
                      ? 'bg-[#2c3223] text-white shadow-xs'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {tab.label} <span className="text-[10px] opacity-75">({count})</span>
                </button>
              );
            })}
          </div>
        )}
        
        <div className="space-y-6 max-h-[540px] overflow-y-auto pr-1">
          {visibleSections.map((section, sIdx) => (
            <div key={sIdx} className="space-y-3">
              <h5 className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5 bg-slate-100/60 px-3 py-1.5 rounded-lg">
                {section.title.toLowerCase().includes('præsentation') || section.title.toLowerCase().includes('animerede') ? (
                  <i className="fa-light fa-sparkles text-brand" />
                ) : (
                  <i className="fa-light fa-video text-brand" />
                )}
                {section.title}
              </h5>
              <div className="space-y-2">
                {section.videos.map((video) => {
                  const isSelected = video.id === activeVideo?.id;
                  const videoWatchUrl = getVideoUrl(video);

                  return (
                    <a
                      key={video.id}
                      href={videoWatchUrl}
                      onClick={(e) => {
                        // If plain primary left-click without modifier keys, switch inline for fast UX!
                        if (e.button === 0 && !e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey) {
                          e.preventDefault();
                          if (isSelected) {
                            togglePlay();
                          } else {
                            handleVideoSelect(video, true);
                          }
                        }
                      }}
                      className={`w-full text-left p-2.5 rounded-xl border transition-all duration-200 flex gap-3 items-center group relative ${
                        isSelected
                          ? 'bg-white border-brand shadow-md ring-1 ring-brand/20'
                          : 'bg-white hover:bg-slate-50 border-slate-100 hover:border-slate-200 shadow-xs'
                      }`}
                      title={`${video.title} – Højreklik for at kopiere link eller åbne i ny fane`}
                    >
                      {/* Thumbnail */}
                      <div className="relative w-20 h-14 rounded-lg overflow-hidden shrink-0 bg-slate-100 border border-slate-200/60">
                        <img
                          src={video.poster}
                          alt={video.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className={`absolute inset-0 flex items-center justify-center transition-all ${
                          isSelected 
                            ? 'bg-brand/20' 
                            : 'bg-slate-950/20 group-hover:bg-slate-950/30'
                        }`}>
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center shadow-md transition-all ${
                            isSelected 
                            ? 'bg-brand text-white scale-110' 
                            : 'bg-white text-slate-800 group-hover:scale-110'
                          }`}>
                            <i className={`fa-solid ${isSelected && isPlaying ? 'fa-pause text-[10px]' : 'fa-play text-[10px] ml-0.5'}`} />
                          </div>
                        </div>
                      </div>

                      {/* Info */}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-1">
                          <h6 className={`font-bold text-xs leading-snug truncate ${
                            isSelected ? 'text-brand-dark' : 'text-slate-800 group-hover:text-slate-900'
                          }`}>
                            {video.title}
                          </h6>
                          <span className="opacity-0 group-hover:opacity-100 transition-opacity text-[10px] text-slate-400 shrink-0" title="Kan åbnes i ny fane">
                            <i className="fa-light fa-arrow-up-right-from-square" />
                          </span>
                        </div>
                        <p className="text-slate-500 text-[10px] line-clamp-1 mt-0.5">
                          {video.description}
                        </p>
                        <div className="flex items-center justify-between mt-1 text-[9px] text-slate-400">
                          <span className="flex items-center gap-1">
                            <i className="fa-light fa-clock text-[10px]" /> {getDuration(video)} min
                          </span>
                          <div className="flex items-center gap-1">
                            {enableAutoplay && autoplay && nextVideoInQueue && video.id === nextVideoInQueue.id && !isSelected && (
                              <span className="text-[8px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100/90 px-1.5 py-0.5 rounded">
                                Næste
                              </span>
                            )}
                            {isAll && video.productName && (
                              <span className="text-[8px] font-semibold uppercase tracking-wider text-slate-400 bg-slate-100 px-1.5 py-0.2 rounded">
                                {video.productId.toUpperCase()}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </a>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
    );
  };

  if (embedded) {
    return layout === 'grid' ? renderGridSection() : renderStandardContent();
  }

  return (
    <section id="video-section" className="py-20 lg:py-28 bg-slate-50 border-t border-slate-100 scroll-mt-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 mb-6 text-[10px] font-bold uppercase tracking-[0.2em] bg-brand-soft text-[#2c3223] rounded-full">
            <i className="fa-light fa-video text-xs" /> Videotutorials &amp; Guides
          </div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-4 tracking-tight">
            {data.title}
          </h2>
          <p className="text-slate-600 leading-relaxed">
            {data.subtitle}
          </p>
        </div>

        {/* Video Player & List Grid */}
        {layout === 'grid' ? renderGridSection() : renderStandardContent()}
      </div>
    </section>
  );
};

export default VideoLibrary;
